<template>
    <div>
        <v-container fluid class="grey-container cta-container" :style="{ 'min-height': '800px', 'background-size': 'contain', 'background-color': '#E6E6E6', 'background-image': 'url(' + getBackground + ')' }">
            <v-container class="contain-width">

                <v-row>
                    <v-col
                        md="8"
                        offset-md="2"
                    >
                      <h1 class="md-display-2 white--text mb-6">{{ $t('headings.Sign up organisation') }}</h1>
                      <div v-if="this.loggedIn">
                        <v-card
                            class="mx-auto form-card"
                            outlined
                        >

                          <v-card-text class="text--primary">
                            <p class="body-1">{{ $t("You are already logged in") }}. {{ $t("Please") }} <router-link
                                :to="{ name: 'logout' }"
                                exact
                            >{{ $t('links.log out') }}</router-link> {{ $t("if you want to sign up as a different user") }}.</p>
                          </v-card-text>
                        </v-card>
                      </div>
                      <div v-else>
                        <v-card
                                class="mx-auto form-card"
                                outlined
                        >

                            <v-card-text class="text--primary">

                                <ValidationObserver ref="observer">
                                    <v-form v-model="valid" @submit.prevent="handleSubmit">

                                        <h2>{{ $t('headings.Organisation details') }}</h2>

                                        <ValidationProvider vid="organisation_name" :name="$t('labels.Organisation name')" rules="required" v-slot="{ errors }">
                                            <v-text-field
                                                    v-model="organisationName"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Organisation name')"
                                                    required
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <ValidationProvider vid="organisation_phone" :name="$t('labels.Organisation phone')" rules="required" v-slot="{ errors }">
                                            <v-text-field
                                                    v-model="organisationPhone"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Phone number')"
                                                    required
                                                    persistent-hint
                                                    :hint="$t('hints.Include extension if needed')"
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <ValidationProvider vid="organisation_category" :name="$t('labels.Organisation category')" rules="required" v-slot="{ errors }">
                                            <v-select
                                                    :items="categoriesList"
                                                    item-text="name"
                                                    item-value="id"
                                                    v-model="organisationCategory"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Organisation type')"
                                                    required
                                            ></v-select>
                                        </ValidationProvider>

                                        <ValidationProvider vid="affiliate_partner" :name="$t('labels.Super partner')" v-slot="{ errors }">
                                            <v-autocomplete
                                                    :items="affiliateOrganisations"
                                                    item-text="name"
                                                    item-value="id"
                                                    v-model="affiliate_partner"
                                                    :error-messages="errors[0]"
                                                    chips
                                                    persistent-hint
                                                    :hint="$t('hints.Selected partner can view your details')"
                                            >
                                                <template v-slot:label>
                                                    <div>
                                                        {{ $t('labels.Super partner associated with') }} <small>({{ $t("labels.optional") }})</small>
                                                    </div>
                                                </template>
                                            </v-autocomplete>
                                        </ValidationProvider>

                                        <ValidationProvider vid="postcode" :name="$t('labels.Postcode')" rules="required" v-slot="{ errors }">
                                            <v-text-field
                                                    v-model="postcode"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Postcode')"
                                                    required
                                            ></v-text-field>
                                            <v-btn v-if="brand === 'findmykit'" @click="postcodeLookup" class="mb-6">{{ $t('buttons.Lookup address from postcode') }}</v-btn>
                                        </ValidationProvider>

                                        <v-select
                                                v-if="addresses.length > 0"
                                                :items="addresses"
                                                :label="$t('labels.Choose address')"
                                                v-on:change="setAddress"
                                                autofocus
                                        ></v-select>

                                        <ValidationProvider vid="address1" :name="$t('labels.Address line 1')" rules="required" v-slot="{ errors }">
                                            <v-text-field
                                                    v-if="showAddress"
                                                    v-model="address1"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Address line 1')"
                                                    required
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <ValidationProvider vid="address2" :name="$t('labels.Address line 2')" v-slot="{ errors }">
                                            <v-text-field
                                                    v-if="showAddress"
                                                    v-model="address2"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Address line 2')"
                                                    required
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <ValidationProvider vid="town" :name="$t('labels.Town')" rules="required" v-slot="{ errors }">
                                            <v-text-field
                                                    v-if="showAddress"
                                                    v-model="town"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Town')"
                                                    required
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <ValidationProvider vid="county" :name="$t('labels.County')" rules="required" v-slot="{ errors }">
                                            <v-text-field
                                                    v-if="showAddress"
                                                    v-model="county"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.County')"
                                                    required
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <ValidationProvider vid="country" :name="$t('labels.Country')" rules="required" v-slot="{ errors }">
                                            <v-select
                                                    v-if="showAddress"
                                                    :items="countriesList"
                                                    item-text="name"
                                                    item-value="code"
                                                    v-model="country"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Country')"
                                                    required
                                            ></v-select>
                                        </ValidationProvider>

                                        <h2 class="mt-12">Administrator details</h2>
                                        <ValidationProvider vid="name" :name="$t('labels.Name')" rules="required" v-slot="{ errors }">
                                            <v-text-field
                                                    v-model="name"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Name')"
                                                    required
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <ValidationProvider vid="email" :name="$t('labels.Email')" rules="required|email" v-slot="{ errors }">
                                            <v-text-field
                                                    v-model="email"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Email')"
                                                    required
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <ValidationProvider vid="email_confirmation" :name="$t('labels.Email confirmation')" v-slot="{ errors }" rules="required|confirmed:email">
                                            <v-text-field
                                                    v-model="email_confirmation"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Confirm email')"
                                                    required
                                                    autocapitalize="none"
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <div class="d-flex align-start">
                                          <div style="width: 80px">
                                            <v-autocomplete
                                                class="mr-2"
                                                ref="areaCode"
                                                v-model="area"
                                                :items="countryCodes"
                                                item-text="name"
                                                item-value="code"
                                            >
                                              <template v-slot:selection="data">
                                                <span class="select-flag"><gb-flag :code="data.item.lang" size="mini" /></span>
                                              </template>
                                              <template v-slot:item="{ item }">
                                                <span class="select-flag"><gb-flag :code="item.lang" size="mini" /><span class="ml-3">{{ item.name }}</span></span>
                                              </template>
                                            </v-autocomplete>
                                          </div>
                                          <div style="width: 100%">
                                            <ValidationProvider vid="mobile" :name="$t('labels.Mobile')" :rules="{ required: true, regex: /^[+][0-9]+$/ }" v-slot="{ errors }">
                                              <v-text-field
                                                  v-model="mobile"
                                                  :error-messages="errors[0]"
                                                  :label="$t('labels.Mobile number (digits only)')"
                                                  required
                                                  persistent-hint
                                                  :placeholder="mobilePlaceholder"
                                                  :hint="$t('labels.Required for SMS notifications')"
                                                  @input="updateArea(mobile)"
                                              >
                                              </v-text-field>
                                            </ValidationProvider>
                                          </div>
                                        </div>

                                        <ValidationProvider vid="terms" :name="$t('labels.Terms')" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
                                          <v-checkbox
                                              v-model="terms"
                                              :error-messages="errors[0]"
                                              required
                                          >
                                            <template v-slot:label>
                                              <div>
                                                {{ $t("labels.I have read the") }}
                                                <v-tooltip bottom>
                                                  <template v-slot:activator="{ on }">
                                                    <a
                                                        @click.prevent.stop="dialog = true"
                                                        v-on="on"
                                                    >
                                                      {{ $t('links.terms and conditions') }}
                                                    </a>
                                                    <v-dialog
                                                        v-model="dialog"
                                                        max-width="600"
                                                    >
                                                      <v-card>
                                                        <v-card-title class="headline">{{ $t('headings.Terms and conditions') }}</v-card-title>

                                                        <v-card-text>
                                                          <TermsContentOrganisation/>
                                                        </v-card-text>

                                                        <v-card-actions>
                                                          <v-spacer></v-spacer>

                                                          <v-btn
                                                              color="green darken-1"
                                                              text
                                                              @click="rejectTerms"
                                                          >
                                                            {{ $t('buttons.Disagree') }}
                                                          </v-btn>

                                                          <v-btn
                                                              color="green darken-1"
                                                              tile
                                                              dark
                                                              elevation="0"
                                                              @click="acceptTerms"
                                                          >
                                                            {{ $t('buttons.Agree') }}
                                                          </v-btn>
                                                        </v-card-actions>
                                                      </v-card>
                                                    </v-dialog>
                                                  </template>
                                                  {{ $t("Opens in popover") }}
                                                </v-tooltip>
                                              </div>
                                            </template>
                                          </v-checkbox>
                                        </ValidationProvider>

                                        <ValidationProvider vid="password" :name="$t('labels.Password')" rules="required|min:8" v-slot="{ errors }">
                                            <v-text-field
                                                    v-model="password"
                                                    :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                                                    :type="showPassword ? 'text' : 'password'"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Password')"
                                                    required
                                                    autocapitalize="none"
                                                    @click:append="showPassword = !showPassword"
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <ValidationProvider v-slot="{ errors }" vid="passwordConfirmation" :name="$t('labels.Password confirmation')">
                                            <v-text-field
                                                    v-model="password_confirmation"
                                                    :type="showPassword ? 'text' : 'password'"
                                                    :error-messages="errors[0]"
                                                    :label="$t('labels.Password confirmation')"
                                                    required
                                                    autocapitalize="none"
                                            ></v-text-field>
                                        </ValidationProvider>

                                        <vue-programmatic-invisible-google-recaptcha
                                                ref="invisibleRecaptcha1"
                                                :sitekey="'6Lc1or0UAAAAAHToK0_wzBIEmJSp2BHGNh35DzKL'"
                                                :elementId="'invisibleRecaptcha1'"
                                                :badgePosition="'left'"
                                                :showBadgeMobile="false"
                                                :showBadgeDesktop="true"
                                                @recaptcha-callback="recaptchaCallback"
                                        ></vue-programmatic-invisible-google-recaptcha>

                                        <div class="d-flex justify-end">
                                            <v-btn v-if="recaptchaFailed" @click="resetRecaptcha" color="error">reCAPTCHA failed: reset</v-btn>

                                            <v-btn tile elevation="0" color="primary" @click="handleSubmit">{{ $t('buttons.Submit') }}</v-btn>
                                        </div>

                                    </v-form>
                                </ValidationObserver>
                            </v-card-text>
                            <v-card-actions>
                                <v-btn text icon color="primary" @click="navigate('sign-up')">
                                    <v-icon>mdi-arrow-left</v-icon>
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                      </div>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>
        <Footer />
        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
            {{ snackText }}
            <v-btn text @click="snack = false">{{ $t('buttons.Close') }}</v-btn>
        </v-snackbar>
    </div>
</template>

<script>
    import {mapState, mapActions, mapGetters} from 'vuex'
    import axios from 'axios';

    import { store } from '../store';

    import { extend } from 'vee-validate';
    import { required, email, min, confirmed } from 'vee-validate/dist/rules';
    import Footer from './Footer'
    import TermsContentOrganisation from "./TermsContentOrganisation";
    import {i18n} from "../i18n";

    extend('confirmed', confirmed);

    // Add the required rule
    extend('required', required);

    // Add the email rule
    extend('email', email);

    // Add the min rule
    extend('min', min);

    export default {
        name: "RegisterOrganisation",
        metaInfo() {
          return {
            title: this.capitalizeFirstLetter(this.brand) + ' - ' + i18n.t('headings.Sign up organisation'),
            htmlAttrs: {
              lang: i18n.locale
            },
            meta: [
              { name: 'description', content: i18n.t('Sign up as an organisation to start scanning our Smart tags') + '.' + i18n.t('Lost property in schools, leisure centres, sports clubs, gyms; {brand} has a revolutionary solution', {brand: this.capitalizeFirstLetter(this.brand)}) + '.' }
            ]
          }
        },
        components: {
          TermsContentOrganisation,
            'Footer': Footer
        },
        data: () => ({
            dialog: false,
            snack: false,
            snackColor: '',
            snackText: '',
            organisationName: '',
            organisationPhone: '',
            organisationCategory: '',
            showAddress: true,
            postcode: '',
            address1: '',
            address2: '',
            town: '',
            county: '',
            country: '',
            name: '',
            email: '',
            email_confirmation: '',
            mobile: '',
            terms: false,
            password: '',
            password_confirmation: null,
            submitted: false,
            valid: false,
            showPassword: false,
            show: false,
            recaptchaFailed: false,
            categories: [
                {name: 'Educational', id: 1},
                {name: 'Transport & travel', id: 2},
                {name: 'Sports and leisure', id: 3},
                {name: 'Retail', id: 4},
                {name: 'Other', id: 5},
                {name: 'Affiliate partner', id: 6}
            ],
            addresses: [],
            coordinates: {},
            affiliateOrganisations: [],
            affiliate_partner: null,
            affiliateSlug: null,
            area: ''
        }),
        mounted () {

            this.$gtag.pageview({
                page_path: '/register-organisation'
            })

            document.getElementById("scrolling-content").scrollTop = 0

            if (this.$router.currentRoute.query.affiliate) {
                this.affiliateSlug = this.$router.currentRoute.query.affiliate
            }

            const instance = axios.create({
                baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST,
                headers: {'Accept-Language': i18n.locale}
              });

              instance
                  .get('/api/organisation-categories')
                .then(response => {
                    this.categories = response.data.data
                })
                .catch(error => {
                    console.log(error)
                })
                .finally(() => {
                    this.show = true
                    window.prerenderReady = true;
                })

              instance
                  .get('/api/organisations/affiliates?brand=' + this.brand)
                .then(response => {
                    if (response.status === 200) {
                        this.affiliateOrganisations = response.data.data
                        console.log(this.affiliateOrganisations)

                        for (let x in this.affiliateOrganisations) {
                            if (this.affiliateOrganisations[x].slug && this.affiliateSlug && this.affiliateOrganisations[x].slug === this.affiliateSlug) {
                                this.affiliate_partner = this.affiliateOrganisations[x].id
                            }
                        }
                    }
                })
                .catch(error => {
                    console.log(error)
                })
                .finally(() => {
                    this.show = true
                    window.prerenderReady = true;
                })
        },
        watch: {
          area(val) {
            if (val !== '') {
              this.mobile = val
              this.$refs.areaCode.blur();
            }
          }
        },
        methods: {
            ...mapActions('account', ['registerOrganisation']),
            capitalizeFirstLetter(string) {
              return string.charAt(0).toUpperCase() + string.slice(1);
            },
            updateArea(mobile) {
              if (mobile.length === 1 && mobile.substring(0, 1) !== '+') {
                this.mobile = '+' + this.mobile
              }

              if (mobile.substring(0, 1) === '+' && mobile.length >= 3) {
                let prefix = mobile.substring(0, 3)

                let area = this.countryCodes.find( record => record.code === prefix)
                if (area) {
                  this.area = area.code
                }
              }
            },
            acceptTerms() {
              this.dialog = false
              this.terms = true
            },
            rejectTerms() {
              this.dialog = false
              this.terms = false
            },
            handleSubmit () {
                this.submitted = true;

                this.$refs.observer.validate().then(success => {
                    if (this.valid) {
                        const { organisationName, organisationPhone, organisationCategory, postcode, address1, town, county, country, name, email, email_confirmation, mobile, password, password_confirmation } = this;
                        if (organisationName && organisationPhone && organisationCategory && postcode && address1 && town && county && country && name && email && email_confirmation && mobile && password && password_confirmation) {
                            this.$refs.invisibleRecaptcha1.execute()
                        }
                    }
                }).catch(error => {
                    console.log(error)
                })
            },
            recaptchaCallback (recaptchaToken) {

                const { organisationName, organisationPhone, organisationCategory, affiliate_partner, postcode, address1, address2, town, county, country, name, email, email_confirmation, mobile, password, password_confirmation, latitude, longitude, terms, brand } = this;
                if (organisationName && organisationPhone && organisationCategory && postcode && address1 && town && county && country && name && email && email_confirmation && mobile && password && password_confirmation && recaptchaToken, terms) {

                    this.registerOrganisation({ organisationName, organisationPhone, organisationCategory, affiliate_partner, postcode, address1, address2, town, county, country, name, email, email_confirmation, mobile, password, password_confirmation, recaptchaToken, latitude, longitude, terms, brand }).then(user => {

                        if (!user) {

                            // Add the server side errors for each field.
                            this.$refs.observer.setErrors(store.getters.validationErrors);

                            this.snack = true
                            this.snackColor = 'error'
                            this.snackText = i18n.t('Please check the form for errors')

                            if (store.getters.validationErrors['recaptchaToken']) {
                                this.recaptchaFailed = true;
                            }
                        }
                    })
                }
            },
            resetRecaptcha () {
                this.$refs.invisibleRecaptcha1.reset()
                this.recaptchaFailed = false;
            },
            postcodeLookup () {
                const { postcode } = this;

                if (postcode) {

                    this.addresses = []

                    axios
                        .get('https://api.getAddress.io/find/' + postcode + '?api-key=' + process.env.VUE_APP_GET_ADDRESS_API_KEY)
                        .then(response => {
                            console.log(response.data)
                            this.addresses = response.data.addresses
                            this.coordinates = {
                                'latitude': response.data.latitude,
                                'longitude': response.data.longitude
                            }
                            //this.showAddress = true
                        })
                        .catch(error => {
                            console.log(error)
                        })
                }
            },
            setAddress (a) {
                console.log(a)
                this.addresses = []
                let address = a.split(",");
                this.address1 = address[0]
                this.address2 = address[1]
                this.town = address[5]
                this.county = address[6]
                this.country = 'GB'
                this.latitude = this.coordinates.latitude ? this.coordinates.latitude : null
                this.longitude = this.coordinates.longitude ? this.coordinates.longitude : null
            },
            navigate(routeName) {
                this.$router.push({name: routeName})
            }
        },
        computed: {
            ...mapGetters('account', ['loggedIn']),
            ...mapState({
              brand: state => state.brand,
              brandName: state => state.brandName
            }),
            getBackground() {
              if (this.brand === 'neotag') {
                return require('@/assets/neotag-background.jpg')
              }
              else {
                return require('@/assets/neotag-background.jpg')
              }
            },
            mobilePlaceholder() {
              if (this.brand === 'neotag') {
                return '+34 000 000 000'
              }
              else {
                return '+44 00000 000 000'
              }
            },
            countryCodes() {
              return [
                {
                  name: 'United Kingdom +44',
                  lang: 'gb',
                  code: '+44'
                },
                {
                  name: 'United States +1',
                  lang: 'us',
                  code: '+1'
                },
                {
                  name: 'Spain +34',
                  lang: 'es',
                  code: '+34'
                },
                {
                  name: 'France +33',
                  lang: 'fr',
                  code: '+33'
                },
                {
                  name: 'Italy +39',
                  lang: 'it',
                  code: '+39'
                },
                {
                  name: 'Portugal +351',
                  lang: 'pt',
                  code: '+351'
                }
              ]
            },
            categoriesList () {
                // return [{name: 'Educational', id: 1},{name: 'Transport & travel', id: 2}]
                return this.categories
            },
            countriesList () {
              return [
                {name: i18n.t('United Kingdom'), code: 'GB'},
                {name: i18n.t('Singapore'), code: 'SG'},
                {name: i18n.t('Spain'), code: 'ES'},
                {name: i18n.t('United States'), code: 'US'},
              ]
            }
        }
    }
</script>

<style scoped>
    button {
        text-transform: none;
    }
    .product-card {
        border-radius: 15px 15px 0 0;
        border: none !important;
    }
</style>
